<template>
    <div class="row">
        <div class="col">
            <div class="card text-sm">
                <div class="card-body">
                    <div class="d-flex flex-column flex-md-row justify-content-between">
                        <h5 class="mb-0">Patient Details</h5>
                        <div>
                            <router-link v-if="!hideView" :to="{name: 'ViewPatient', params: {id: patient.id}}" class="btn btn-cancel sm-shadow mt-4 mt-md-0 px-4 mr-2">View patient</router-link>
                            <router-link v-if="!hideEdit" :to="{name: 'EditPatient', params: {id: patient.id}}" class="btn btn-primary sm-shadow mt-4 mt-md-0 px-4">Edit patient</router-link>
                        </div>
                    </div>

                    <div class="row mt-3">
                        <div class="col-lg-6 pr-lg-6">
                            <div class="row">
                                <div class="col-sm-3 font-weight-medium">Name</div>
                                <div class="col-sm-9 text-capitalize">{{ `${patient.firstName} ${patient.middleNames ? patient.middleNames : ''} ${patient.surname}` }}</div>
                            </div>
                            
                            <hr>

                            <div v-if="patient.dateOfBirth">
                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">DOB</div>
                                    <div class="col-sm-9">{{ patient.dateOfBirth | moment("DD/MM/YYYY") }}</div>
                                </div>

                                <hr>
                            </div>

                            <div class="row">
                                <div class="col-sm-3 font-weight-medium">{{ getIdentifierLabel() }}</div>
                                <div class="col-sm-9">{{ patient.identifier }}</div>
                            </div>

                            <div v-if="gdEstimatedDeliveryDate != null">
                                <hr>

                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Baby estimated delivery date (EDD)</div>
                                    <div class="col-sm-9">
                                        {{ formatDate(gdEstimatedDeliveryDate) }} <span v-if="formatGA(gdEstimatedDeliveryDate) != null">| Gestational age in weeks = {{ formatGA(gdEstimatedDeliveryDate) }}</span>
                                    </div>
                                </div>
                            </div>

                            <hr>

                            <div class="row">
                                <div class="col-sm-3 font-weight-medium">Phone no</div>
                                <div class="col-sm-9">{{ patient.phone }}</div>
                            </div>

                            <hr>

                            <div class="row">
                                <div class="col-sm-3 font-weight-medium">Email</div>
                                <div class="col-sm-9">{{ patient.email }}</div>
                            </div>

                            <hr>

                            <div class="row">
                                <div class="col-sm-3 font-weight-medium">Address</div>
                                <div class="col-sm-9">
                                    {{ formatAddress(patient.address) }}
                                </div>
                            </div>

                            <div v-if="shippingAddress && Object.keys(shippingAddress).length">
                                <hr>

                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Shipping address</div>
                                    <div class="col-sm-9">
                                        {{ formatShippingAddress(shippingAddress) }}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="col-lg-6 pl-lg-6 mt-5 mt-lg-0">
                            <div v-if="patient.height">
                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Height</div>
                                    <div class="col-sm-9">{{ convertCentimetresToFeetAndInches(patient.height) }} | {{ patient.height }}cm</div>
                                </div>

                                <hr>
                            </div>

                            <div v-if="patient.weight">
                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Weight</div>
                                    <div class="col-sm-9">{{ convertKilosToStonesAndPounds(patient.weight) }} | {{ patient.weight }}kg </div>
                                </div>

                                <hr>
                            </div>

                            <div class="row">
                                <div class="col-sm-3 font-weight-medium">Gender</div>
                                <div v-if="patient.gender == Gender.MALE" class="col-md-9">Male</div>
                                <div v-if="patient.gender == Gender.FEMALE" class="col-md-9">Female</div>
                                <div v-if="patient.gender == Gender.OTHER" class="col-md-9">Other / prefer not to say</div>
                            </div>

                            <div v-if="patient.primaryEthnicity != null">
                                <hr>

                                <div class="row">
                                    <div class="col-sm-3 font-weight-medium">Ethnicity group</div>
                                    <div class="col-md-9">
                                        <div v-if="patient.primaryEthnicity != null">Primary ethnicity: {{ getPrimaryEthnicityLabel(patient.primaryEthnicity) }}</div>
                                        <div v-if="patient.secondaryEthnicity != null">Secondary ethnicity: {{ getSecondaryEthnicityLabel(patient.primaryEthnicity, patient.secondaryEthnicity) }}</div>
                                        <div v-if="patient.tertiaryEthnicity != null">Tertiary ethnicity: {{ getTertiaryEthnicityLabel(patient.primaryEthnicity, patient.secondaryEthnicity, patient.tertiaryEthnicity) }}</div>
                                    </div>
                                </div>
                            </div>

                            <hr>

                            <div class="row">
                                <div class="col-md-3">Preferred Language</div>
                                <div class="col-md-9">{{ formatLanguageLabel(patient.preferredLanguage) }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import WeightConversionMixin from '../mixins/WeightConversionMixin'
import FormatterMixin from '../mixins/FormatterMixin'
import Gender from '../../Common/enums/Gender'
export default {
    name: 'PatientDetailsComponent',

    props: {
        patient: Object,
        organisation: Object,
        shippingAddress: Object,
        gdEstimatedDeliveryDate: String,
        hideEdit: Boolean(false),
        hideView: Boolean(false)
    },

    mixins: [
        WeightConversionMixin,
        FormatterMixin
    ],

    data () {
        return {
            Gender
        }
    },

    computed: {
        ...mapGetters({
            patientIdentifierLabel: 'OrganisationStore/GetPatientIdentifierLabel',
            ethnicities: 'EthnicityStore/GetEthnicities',
            languages: 'LanguageStore/GetLanguages'
        })
    },

    methods: {

        getIdentifierLabel() {
            return this.organisation ? this.organisation.patientIdentifierLabel : this.patientIdentifierLabel
        },

        getPrimaryEthnicityLabel (code) {
            const found = this.ethnicities.find(x => x.code === code)
            if (!found) return null
            return found.label
        },

        getSecondaryEthnicityLabel (primaryCode, secondaryCode) {
            const primary = this.ethnicities.find(x => x.code === primaryCode)
            if (!primary) return null

            const secondary = primary.secondaryEthnicityDefinitions.find(x => x.code === secondaryCode)
            if (!secondary) return null
            return secondary.label
        },

        getTertiaryEthnicityLabel (primaryCode, secondaryCode, tertiaryCode) {
            const primary = this.ethnicities.find(x => x.code === primaryCode)
            if (!primary) return null

            const secondary = primary.secondaryEthnicityDefinitions.find(x => x.code === secondaryCode)
            if (!secondary) return null

            const tertiary = secondary.tertiaryEthnicityDefinitions.find(x => x.code === tertiaryCode)
            if (!tertiary) return null
            return tertiary.label
        }
    }
}
</script>